import { useNavigation } from '@react-navigation/native';
import React from 'react';
import { RefreshControl, ScrollView, TouchableOpacity, View } from 'react-native';

import { getPickListStatusLabel } from '../../../common/utils/pickList/getPickListStatusLabel';
import { formatShiftRange } from '../../../common/utils/people/roster';
import { printMillisecondsNicely } from '../../../common/utils/time/time';
import { formatNumber } from '../../../common/utils/utils';

import {
	Button,
	Heading,
	Label,
	Spinner,
	Text,
	TextInput,
} from '../../components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import PickerLabelDialogs from './PickerLabelDialogs';
import { createPickerMyDayViewStyles } from './PickerMyDayView.styles';
import usePickerMyDay, {
	isRowAssignedToPicker,
	isRowIncompletePick,
	isRowLabelOnly,
	isRowPartiallyPicked,
} from './usePickerMyDay';

const PickerMyDayView = ({ refetchModeData, onViewEmployeeDay, onBackToMyDay }) => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createPickerMyDayViewStyles);
	const pickerDay = usePickerMyDay({ refetchModeData });
	const {
		userId,
		todayDisplayLabel,
		isFirstLoad,
		refreshPage,
		isRefreshing,
		canUseClockStatus,
		isClockStatusReliable,
		clockStatus,
		rosterShift,
		shouldHideWorkForClock,
		openClockPass,
		canViewPendingStock,
		isMineMode,
		showMine,
		showAll,
		myCount,
		allCount,
		searchValue,
		setSearchValue,
		routeMachineCount,
		pendingCount,
		labelOnlyCount,
		completedCount,
		myStats,
		filteredRouteGroups,
		filteredCompletedRouteGroups,
		hasCompletedWork,
		openPickingWork,
		openCompletedPick,
		printingLabelDocketId,
	} = pickerDay;
	const routeCount = filteredRouteGroups.length;

	if (isFirstLoad) {
		return <Spinner />;
	}

	return (
		<ScrollView
			style={styles.container}
			scrollIndicatorInsets={{ right: 1 }}
			refreshControl={(
				<RefreshControl
					refreshing={isRefreshing}
					onRefresh={refreshPage}
				/>
			)}
		>
			<View style={styles.content}>
				<View style={styles.headerCard}>
					<Heading
						heading="My day"
						subHeading={`Picking work for ${todayDisplayLabel}`}
					/>
					{
						canUseClockStatus && (
							<View style={styles.clockRow}>
								<Label
									label={isClockStatusReliable
										? clockStatus.isClockedIn
											? 'Clocked in'
											: clockStatus.hasClockedOutToday
												? 'Clocked out'
												: 'Not clocked in'
										: 'Clock status unavailable'}
									green={isClockStatusReliable && clockStatus.isClockedIn}
									secondary={!isClockStatusReliable || (!clockStatus.isClockedIn && clockStatus.hasClockedOutToday)}
									yellow={isClockStatusReliable && !clockStatus.isClockedIn && !clockStatus.hasClockedOutToday}
									style={styles.clockChip}
								/>
								{
									Boolean(rosterShift) && (
										<Label
											label={`Shift ${formatShiftRange(rosterShift.startMinutes, rosterShift.endMinutes)}`}
											secondary={true}
											style={styles.clockChip}
										/>
									)
								}
							</View>
						)
					}
					{
						Boolean(onViewEmployeeDay) && (
							<Button
								label="View an employee's day"
								onPress={onViewEmployeeDay}
								style={styles.employeePreviewButton}
							/>
						)
					}
					{
						Boolean(onBackToMyDay) && (
							<Button
								label="Back to my day"
								onPress={onBackToMyDay}
								secondary={true}
								style={styles.employeePreviewButton}
							/>
						)
					}
				</View>
				{
					shouldHideWorkForClock
						? (
							<View style={styles.clockInCard}>
								<Text style={styles.clockInTitle}>
									{clockStatus.hasClockedOutToday ? 'Clock back in to see your picks' : 'Clock in to see your picks'}
								</Text>
								<Text style={styles.clockInText}>Use Shift pass to clock in, then your assigned routes will appear here.</Text>
								<Button
									label="Open shift pass"
									onPress={openClockPass}
									secondary={true}
								/>
							</View>
						)
						: (
							<>
								<View style={styles.queueControlsCard}>
									<Text style={styles.subtleText}>
										{
											isMineMode
												? 'Showing routes with your remaining work. Coworkers\' remaining machines are view-only.'
												: 'Showing remaining picks and repair-only labels in today\'s picking queue.'
										}
									</Text>
									<View style={styles.viewModeRow}>
										<TouchableOpacity
											style={[styles.viewModeChip, isMineMode && styles.viewModeChipActive]}
											onPress={() => showMine()}
										>
											<Text style={[styles.viewModeChipText, isMineMode && styles.viewModeChipTextActive]}>
												Mine ({formatNumber(myCount)})
											</Text>
										</TouchableOpacity>
										<TouchableOpacity
											style={[styles.viewModeChip, !isMineMode && styles.viewModeChipActive]}
											onPress={() => showAll()}
										>
											<Text style={[styles.viewModeChipText, !isMineMode && styles.viewModeChipTextActive]}>
												All ({formatNumber(allCount)})
											</Text>
										</TouchableOpacity>
									</View>
									<View style={styles.statsRow}>
										<View style={[styles.statCard, styles.statCardFirst]}>
											<Text style={styles.statValue}>{formatNumber(routeMachineCount)}</Text>
											<Text style={styles.statLabel}>Route machines</Text>
										</View>
										<View style={styles.statCard}>
											<Text style={styles.statValue}>{formatNumber(pendingCount)}</Text>
											<Text style={styles.statLabel}>Pending</Text>
										</View>
										<View style={styles.statCard}>
											<Text style={styles.statValue}>{formatNumber(labelOnlyCount)}</Text>
											<Text style={styles.statLabel}>Label only</Text>
										</View>
										<View style={[styles.statCard, styles.statCardLast]}>
											<Text style={styles.statValue}>{formatNumber(completedCount)}</Text>
											<Text style={styles.statLabel}>Completed POS</Text>
										</View>
									</View>
									{
										Boolean(myStats && (myStats.today?.posCount > 0 || myStats.last30Days?.posCount > 0)) && (
											<View style={styles.myStatsCard}>
												<Text style={styles.myStatsTitle}>My picking stats</Text>
												<Text style={styles.myStatsText}>
													Today: {formatNumber(myStats.today?.posCount || 0)} POS · {formatNumber(myStats.today?.units || 0)} units
													{myStats.today?.pickingTimeMilliseconds > 0 ? ` · ${printMillisecondsNicely(myStats.today.pickingTimeMilliseconds)}` : ''}
												</Text>
												{
													myStats.last30Days?.posCount > 0 && (
														<Text style={styles.myStatsText}>
															30 days: {printMillisecondsNicely(myStats.last30Days.averageTimePerPOSMilliseconds || 0) || '0 s'} per POS · {printMillisecondsNicely(myStats.last30Days.averageTimePer100UnitsMilliseconds || 0) || '0 s'} per 100 units · {myStats.last30Days.averageUnitsPerActiveDay || 0} units/day
														</Text>
													)
												}
											</View>
										)
									}
									<View style={styles.searchCard}>
										<TextInput
											value={searchValue}
											onChange={setSearchValue}
											placeholder="Search by machine, route, driver, picker, or date"
										/>
									</View>
									<View style={styles.headerActionsRow}>
										<Label label={`${routeCount} routes`} primary={true} />
										<Button
											label="Open service runs"
											onPress={() => navigation.push('ServiceRunListView')}
										/>
									</View>
								</View>
								{
									canViewPendingStock && (
										<TouchableOpacity
											style={styles.pendingStockCard}
											activeOpacity={0.86}
											onPress={() => navigation.push('PendingStockListView')}
										>
											<View style={styles.rowTitleLine}>
												<Text style={styles.rowTitle}>Pending stock</Text>
												<Label label="Open" primary={true} />
											</View>
											<Text style={styles.rowMeta}>Process driver returns back into the warehouse.</Text>
										</TouchableOpacity>
									)
								}
								<View style={styles.listSectionHeader}>
									<Text style={styles.listSectionTitle}>Routes to prepare</Text>
									<Label label={`${filteredRouteGroups.length} routes`} primary={true} />
								</View>
								{
									filteredRouteGroups.length === 0 && (
										<View style={styles.emptyCard}>
											<Text style={styles.subtleText}>
												{
													searchValue.trim()
														? 'No picking routes found with this search.'
														: isMineMode
															? 'No picking or label work is assigned to you for today.'
															: 'No picking or label work is planned for today.'
												}
											</Text>
										</View>
									)
								}
								{
									filteredRouteGroups.map((group) => {
										const ownedRowCount = group.rows.filter(row => (
											isRowAssignedToPicker(row, userId)
										)).length;
										const pickerNames = group.assignedPickers.length
											? group.assignedPickers
												.map((picker) => {
													return picker.id === userId ? `${picker.name} (you)` : picker.name;
												})
												.join(', ')
											: 'No pickers assigned';

										return (
											<View key={group.id} style={styles.routeCard}>
												<View style={styles.routeTitleLine}>
													<Text style={styles.routeTitle}>{group.routeName}</Text>
													<Label
														label={`${ownedRowCount} of ${group.rows.length} yours`}
														primary={ownedRowCount > 0}
														secondary={ownedRowCount === 0}
													/>
												</View>
												<Text style={styles.routePlanned}>{group.plannedLabel}</Text>
												<View style={styles.routeMetaLine}>
													<Text style={styles.routeMetaLabel}>Driver</Text>
													<Text style={styles.routeMeta}>{group.driverName}</Text>
												</View>
												<View style={styles.routeMetaLine}>
													<Text style={styles.routeMetaLabel}>Packing</Text>
													<Text style={styles.routeMeta}>{pickerNames}</Text>
												</View>
												{
													group.rows.map((row, rowIndex) => {
														const isOwned = isRowAssignedToPicker(row, userId);
														const isPrintingLabel = printingLabelDocketId === row.id;
														const routeOrder = rowIndex + 1;

														return (
															<TouchableOpacity
																key={row.id}
																style={[
																	styles.machineCard,
																	row.inventoryPicked
																		? styles.rowCardDone
																		: isRowLabelOnly(row)
																			? styles.rowCardLabelOnly
																			: isRowPartiallyPicked(row)
																				? styles.rowCardPartiallyPicked
																				: styles.rowCardPending,
																	!isOwned && styles.machineCardViewOnly,
																]}
																activeOpacity={0.86}
																disabled={!isOwned || isPrintingLabel}
																onPress={() => openPickingWork(row)}
															>
																{
																	Boolean(row.packTags.length) && (
																		<View style={styles.pickingLabels}>
																			<Text style={styles.pickingLabelsTitle}>Picking label</Text>
																			<View style={styles.pickingLabelsList}>
																				{
																					row.packTags.map(tag => (
																						<View key={tag.id} style={styles.pickingLabelChip}>
																							<Text style={styles.pickingLabelText}>{tag.name}</Text>
																						</View>
																					))
																				}
																			</View>
																		</View>
																	)
																}
																<View style={styles.machineTitleLine}>
																	<View style={styles.orderBadge}>
																		<Text style={styles.orderBadgeText}>{routeOrder}</Text>
																	</View>
																	<View style={styles.machineTitleContent}>
																		<Text style={styles.machineName}>{row.machineName}</Text>
																		<Text style={styles.machineLocation}>
																			{row.locationName || row.placeName || row.assetId || 'No location details'}
																		</Text>
																	</View>
																	<Label
																		label={isRowLabelOnly(row) ? 'Label only' : getPickListStatusLabel(row)}
																		green={row.inventoryPicked}
																		yellow={isRowIncompletePick(row)}
																		primary={(!row.inventoryPicked && !isRowIncompletePick(row)) || isRowLabelOnly(row)}
																	/>
																</View>
																<View style={styles.machineAssignmentLine}>
																	<Label
																		label={isOwned ? row.labelOnly ? 'Your label' : 'Your pick' : row.pickerName || 'Unassigned'}
																		primary={isOwned}
																		secondary={!isOwned}
																	/>
																	<Text style={styles.machineActionText}>
																		{isOwned ? row.labelOnly ? isPrintingLabel ? 'Queueing label…' : 'Tap to send label' : 'Tap to open' : 'View only'}
																	</Text>
																</View>
															</TouchableOpacity>
														);
													})
												}
											</View>
										);
									})
								}
								{Boolean(hasCompletedWork) && (
									<>
										<View style={[styles.listSectionHeader, styles.completedSectionHeader]}>
											<Text style={styles.listSectionTitle}>Completed today</Text>
											<Label label={`${formatNumber(completedCount)} POS`} green={true} />
										</View>
										{filteredCompletedRouteGroups.length === 0 && (
											<View style={styles.emptyCard}>
												<Text style={styles.subtleText}>No completed picks found with this search.</Text>
											</View>
										)}
										{filteredCompletedRouteGroups.map(group => (
											<View key={`completed-${group.id}`} style={styles.routeCard}>
												<View style={styles.routeTitleLine}>
													<Text style={styles.routeTitle}>{group.routeName}</Text>
													<Label label="Completed" green={true} />
												</View>
												<Text style={styles.routePlanned}>{group.plannedLabel}</Text>
												<View style={styles.routeMetaLine}>
													<Text style={styles.routeMetaLabel}>Driver</Text>
													<Text style={styles.routeMeta}>{group.driverName}</Text>
												</View>
												{group.rows.map((row, rowIndex) => {
													const isOwned = isRowAssignedToPicker(row, userId);
													return (
														<TouchableOpacity
															key={row.id}
															style={[
																styles.machineCard,
																styles.rowCardDone,
																!isOwned && styles.machineCardViewOnly,
															]}
															activeOpacity={0.86}
															disabled={!isOwned}
															onPress={() => openCompletedPick(row)}
														>
															<View style={styles.machineTitleLine}>
																<View style={styles.orderBadge}>
																	<Text style={styles.orderBadgeText}>{rowIndex + 1}</Text>
																</View>
																<View style={styles.machineTitleContent}>
																	<Text style={styles.machineName}>{row.machineName}</Text>
																	<Text style={styles.machineLocation}>
																		{row.locationName || row.placeName || row.assetId || 'No location details'}
																	</Text>
																</View>
																<Label label={getPickListStatusLabel(row)} green={row.inventoryPicked} secondary={!row.inventoryPicked} />
															</View>
															<View style={styles.machineAssignmentLine}>
																<Label label="Tub sticker" secondary={true} />
																<Text style={styles.machineActionText}>{isOwned ? 'Tap to view or reprint' : 'Assigned to another picker'}</Text>
															</View>
														</TouchableOpacity>
													);
												})}
											</View>
										))}
									</>
								)}
							</>
						)
				}
			</View>
			<PickerLabelDialogs
				labelDialog={pickerDay.labelDialog}
				completedPickDialog={pickerDay.completedPickDialog}
			/>
		</ScrollView>
	);
};

export default PickerMyDayView;
